import React, { createContext, useContext, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getSystemCurrencyRequest,
  getCurrencyRatesRequest,
} from "../redux/action";
import { currencySymbols } from "../utils/currency";
import axios from "../redux/sagas/axios";

// Fallback currency configuration
const FALLBACK_CURRENCY = "ZMW";
const FALLBACK_EXCHANGE_RATE = 19.5; // 1 USD = 19.50 ZMW

const CurrencyContext = createContext();

export const CurrencyProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [userCurrencyData, setUserCurrencyData] = useState(null);
  const [userCurrencyLoading, setUserCurrencyLoading] = useState(false);

  const { systemCurrency, currencyRates, userToken, currentUser } = useSelector(
    (state) => ({
      systemCurrency: state.currencyData?.systemCurrency,
      currencyRates: state.currencyData?.currencyRates || [],
      userToken: state.userData?.token,
      currentUser: state.userData?.user,
    }),
  );

  // Check if currency data is actually loaded
  const hasSystemCurrency =
    systemCurrency !== null && systemCurrency !== undefined;
  const hasCurrencyRates = currencyRates && currencyRates.length > 0;
  const isDataLoaded =
    hasSystemCurrency &&
    (systemCurrency === "USD" ||
      hasCurrencyRates ||
      systemCurrency === FALLBACK_CURRENCY);

  // Fetch user currency data when user is available
  useEffect(() => {
    const fetchUserCurrency = async () => {
      if (!currentUser?._id || !userToken) return;

      try {
        setUserCurrencyLoading(true);
        const response = await axios.get(
          `/currency/user-currency/${currentUser._id}`,
        );
        setUserCurrencyData(response.data);
      } catch (error) {
        console.error("Error fetching user currency:", error);
        // Don't set error state, just fall back to system currency
        setUserCurrencyData(null);
      } finally {
        setUserCurrencyLoading(false);
      }
    };

    fetchUserCurrency();
  }, [currentUser?._id, userToken]);

  // Fetch currency data when needed
  useEffect(() => {
    const fetchCurrencyData = () => {
      try {
        setError(null);
        dispatch(getSystemCurrencyRequest());
        dispatch(getCurrencyRatesRequest());
        setHasFetched(true);
      } catch (err) {
        console.error("Error fetching currency data:", err);
        setError(err.message);
        setHasFetched(true);
      }
    };

    // Fetch if we haven't fetched yet, or if user just logged in and we don't have data
    if (!hasFetched || (userToken && !hasSystemCurrency)) {
      fetchCurrencyData();
    }
  }, [dispatch, hasFetched, userToken, hasSystemCurrency]);

  // Update isLoaded based on actual data availability or timeout
  useEffect(() => {
    if (hasFetched && isDataLoaded && !isLoaded) {
      setIsLoaded(true);
    }

    // Fallback: if we've fetched but still no data after 5 seconds, assume ZMW
    if (hasFetched && !isLoaded) {
      const timeout = setTimeout(() => {
        if (!isDataLoaded) {
          console.warn(
            "Currency data fetch timeout, falling back to ZMW with rate 1 USD = 19.50 ZMW",
          );
          setIsLoaded(true);
        }
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [isDataLoaded, isLoaded, hasFetched]);

  // Get effective currency based on priority: User Preferred → System → Fallback
  const getEffectiveCurrency = () => {
    // Priority 1: User preferred currency
    if (userCurrencyData?.preferredCurrency) {
      return userCurrencyData.preferredCurrency;
    }

    // Priority 2: System currency
    if (systemCurrency) {
      return systemCurrency;
    }

    // Priority 3: Fallback currency
    return FALLBACK_CURRENCY;
  };

  const effectiveCurrency = getEffectiveCurrency();

  // Get exchange rate for a currency
  const getExchangeRate = (targetCurrency = effectiveCurrency) => {
    if (targetCurrency === "USD") return 1;

    const rate = currencyRates.find(
      (r) => r.targetCurrency === targetCurrency && r.isActive,
    );

    // Use fallback rate if no rate found and currency is ZMW
    if (!rate && targetCurrency === FALLBACK_CURRENCY) {
      return FALLBACK_EXCHANGE_RATE;
    }

    return rate?.rate || 1;
  };

  // Get currency symbol
  const getCurrencySymbol = (currency = effectiveCurrency) => {
    return currencySymbols[currency] || "$";
  };

  // Convert USD to display currency (synchronous)
  const convertUSDToDisplay = (usdAmount) => {
    if (!usdAmount || usdAmount === 0) return 0;
    const rate = getExchangeRate(effectiveCurrency);
    return usdAmount * rate;
  };

  // Convert display currency to USD (synchronous)
  const convertDisplayToUSD = (displayAmount) => {
    if (!displayAmount || displayAmount === 0) return 0;
    const rate = getExchangeRate(effectiveCurrency);
    return displayAmount / rate;
  };

  // Format amount with currency symbol
  const formatAmount = (amount, currency = effectiveCurrency) => {
    const symbol = getCurrencySymbol(currency);
    return `${symbol}${parseFloat(amount).toFixed(2)}`;
  };

  // Convert any amount between currencies
  const convertBetweenCurrencies = (amount, fromCurrency, toCurrency) => {
    if (fromCurrency === toCurrency) return amount;

    // Convert to USD first, then to target currency
    const usdAmount =
      fromCurrency === "USD" ? amount : amount / getExchangeRate(fromCurrency);
    const targetAmount =
      toCurrency === "USD"
        ? usdAmount
        : usdAmount * getExchangeRate(toCurrency);

    return targetAmount;
  };

  // Refresh currency data (useful for manual refresh)
  const refreshCurrencyData = () => {
    setIsLoaded(false);
    setHasFetched(false);
    setError(null);
    setUserCurrencyData(null);
    dispatch(getSystemCurrencyRequest());
    dispatch(getCurrencyRatesRequest());
  };

  const contextValue = {
    // State
    systemCurrency: systemCurrency || FALLBACK_CURRENCY,
    effectiveCurrency,
    userCurrency: userCurrencyData?.preferredCurrency || null,
    currencyRates,
    isLoaded: isLoaded && !userCurrencyLoading,
    error,
    userCurrencyData,

    // Conversion functions
    convertUSDToDisplay,
    convertDisplayToUSD,
    convertBetweenCurrencies,

    // Utility functions
    getCurrencySymbol,
    getExchangeRate,
    formatAmount,
    getEffectiveCurrency,

    // Actions
    refreshCurrencyData,
  };

  return (
    <CurrencyContext.Provider value={contextValue}>
      {children}
    </CurrencyContext.Provider>
  );
};

// Custom hook to use currency context
export const useCurrencyContext = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error(
      "useCurrencyContext must be used within a CurrencyProvider",
    );
  }
  return context;
};

export default CurrencyContext;
